import _ from 'lodash';
import React from 'react';

import {
  Box,
  Button,
  Stack,
  styled
} from '@mui/material';
import {
  fields,
  Form,
  LoadingOverlay,
  modalUtil,
  TabBar,
  Table
} from 'iron-forge-react-modules';
import {
  BaseWidget,
  FormattedCode,
  // isProd,
  routeDirectory,
  selectFields,
  useTabSearchParams
} from 'modules';

import { Archive, Rule, Unarchive } from '@mui/icons-material';
import { ValidationState } from 'generated/graphql';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as ProcessOrdersModal from '../ProcessOrdersModal/ProcessOrdersModal';
import * as dataService from './dataService';
import * as types from './types';



const ListItem = styled('li')(({ theme }) => ({
  color: theme.palette.error.main
}));

const TableComponent = Table.genTableComponent<types.IColumnId>();
const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();

const TabBarBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2)
}));


export interface IProps {
  storeId?: string | null;
  validationState: ValidationState;

  onSelectTab: (validationState: ValidationState) => void;
}

export const FC: React.FC<IProps> = props => {

  const actualStoreId = props?.storeId;

  const { FC: StoreSelectField } = selectFields.StoreSelectField.genComponentAndControllerGenerator({});
  const [dropDownStoreId, setDropDownStoreId] = useTabSearchParams({
    defaultTabId: '',
    paramName: 'storeId'
  });

  // Use the prop storeId if available, otherwise use the dropdown storeId
  const effectiveStoreId = actualStoreId || dropDownStoreId;

  const navigate = useNavigate();
  const form = useForm<types.IFormState>({
    defaultValues: {
      orderNumber: ''
    }
  });
  const {
    modalType2IsOpen,
    setModalIsOpen
  } = modalUtil.useModalTrackers(['processOrders']);
  const {
    pageInfo,
    queryTuple,
    fnQueryPage,
    fnQuery
  } = dataService.useQuery(props.validationState, effectiveStoreId);

  const [
    toggleExternalOrderArchived,
    toggleExternalOrderArchivedTuple
  ] = dataService.useToggleExternalOrderArchived();

  const processSubmit = React.useCallback(
    (formState: types.IFormState) => {
      fnQuery({
        filter: {
          orderNumber: formState.orderNumber
        }
      });
    },
    [fnQuery]
  );



  const handleClickPopoverOption = React.useCallback(
    (orderId: string) => {
      navigate(routeDirectory.admin.validateOrder.path(orderId, effectiveStoreId));
    },
    [
      navigate,
      effectiveStoreId
    ]
  );

  const handleClickToggleArchiveOrder = React.useCallback(
    async (orderId: string) => {
      await toggleExternalOrderArchived(orderId);
    },
    [toggleExternalOrderArchived]
  );

  const rows = React.useMemo(
    (): types.IRow[] => _.map(queryTuple.data.externalOrders, (order): types.IRow => ({
      id: order.id,
      expandedContent: (
        <Stack
          direction="row"
          justifyContent="flex-start"
          spacing={8}>
          <FormattedCode.FC content={order.rawData} />

          <ul>
            {_.map(order.validationErrors, (err, index) => (
              <ListItem key={index}>
                {err}
              </ListItem>)
            )}
          </ul>
        </Stack>
      ),
      columnId2Value: {
        metaCreated: {
          type: 'string',
          label: order.createdAt.toFormat('yyyy-MM-dd @ hh:mm:ss a')
        },
        orderNumber: {
          type: 'string',
          label: order.orderNumber
        },
        actions: {
          type: 'component',
          sortableValue: '',
          label: (
            <>
              <Box display="flex" gap={4}>
                {props.validationState === 'invalid' && effectiveStoreId && (
                  <Button
                    startIcon={<Rule />}
                    size="small"
                    onClick={() => handleClickPopoverOption(order.id)}
                  >
                    Override Validation
                  </Button>

                )}
                {props.validationState === 'invalid' && (
                  <Button
                    color="error"
                    startIcon={<Archive />}
                    size="small"
                    onClick={() => handleClickToggleArchiveOrder(order.id)}
                  >
                    Archive
                  </Button>
                )}
              </Box >
              {props.validationState === 'invalidArchived' && (
                <Button
                  color="primary"
                  startIcon={<Unarchive />}
                  size="small"
                  onClick={() => handleClickToggleArchiveOrder(order.id)}
                >
                  Unarchive
                </Button>
              )}
            </>
          )
        }
      }
    })),
    [queryTuple.data.externalOrders, props.validationState, effectiveStoreId, handleClickPopoverOption, handleClickToggleArchiveOrder]
  );

  const loading = queryTuple.loading || toggleExternalOrderArchivedTuple.loading;
  return (
    <>
      <LoadingOverlay.FC active={loading} />

      <Form.FC
        disableDefaultOverflow
        onSubmit={form.handleSubmit(processSubmit)}>
        <BaseWidget.FC>
          <TabBarBox>
            <TabBar.FC
              tabId={props.validationState}
              tabs={[
                {
                  id: 'valid',
                  label: "Valid"
                },
                {
                  id: 'invalid',
                  label: "Invalid"
                },
                {
                  id: 'invalidArchived',
                  label: "Archived"
                }
              ]}
              onSelectTab={tabId => props.onSelectTab(tabId as ValidationState)} />
          </TabBarBox>

          <Stack
            direction="row"
            justifyContent="space-between">
            <Box width="30%" display="flex" justifyContent="space-between">
              <Stack
                direction="row"
                spacing={2}
                alignItems="center">
                <ControlledTextField
                  disableDefaultMargins
                  formControl={form.control}
                  fieldKey="orderNumber" />

                <Button type="submit">
                  Search
                </Button>
              </Stack>
              <StoreSelectField
                disableDefaultMargins
                label="Store"
                selectProps={{
                  type: 'singleSelect',
                  value: {
                    id: dropDownStoreId ?? '',
                    displayName: ''
                  },
                  onChange: store => setDropDownStoreId(store?.id ?? '')
                }} />
            </Box>
            {effectiveStoreId && (
              <Button onClick={() => setModalIsOpen('processOrders', true)}>
                Process & Cache
              </Button>
            )}
          </Stack>

          <TableComponent
            disableSorting
            paginationOptions={Table.pageInfo2PaginationOptions(pageInfo, fnQueryPage)}
            rows={rows}
            initSortOptions={{
              columnId: 'metaCreated',
              direction: 'descending'
            }}
            columns={_.compact([
              {
                id: 'metaCreated',
                label: "Created At"
              },
              {
                id: 'orderNumber',
                label: 'Order Number'
              },
              props.validationState === 'invalid' || props.validationState === 'invalidArchived' ? {
                id: 'actions',
                label: "Actions",
                width: '33%'
              } : null
            ])}
            onClickRowPopoverOption={handleClickPopoverOption} />
        </BaseWidget.FC>
      </Form.FC>

      {effectiveStoreId && modalType2IsOpen.processOrders && (
        <ProcessOrdersModal.FC
          storeId={effectiveStoreId}
          onClose={didQuery => {
            setModalIsOpen('processOrders', false);
            if (didQuery && queryTuple.refetch) {
              queryTuple.refetch();
            }
          }} />
      )}
    </>
  );
};
